import { type SignIn, type ExperienceSocialConnector, SignInIdentifier } from '@logto/schemas';
import { useId, useMemo, useState } from 'react';

import MailIcon from '@/assets/icons/lucide-mail.svg?react';
import MessageSquareTextIcon from '@/assets/icons/lucide-message-square-text.svg?react';
import VkIdIcon from '@/assets/icons/vk-id.svg?react';
import YandexIdLogo from '@/assets/icons/yandex-id.svg?react';
import IdentifierSignInForm from '@/components/IdentifierSignInForm';
import PasswordSignInForm from '@/components/PasswordSignInForm';
import useSocial from '@/containers/SocialSignInList/use-social';

import styles from './index.module.scss';

type Props = {
  readonly signInMethods: SignIn['methods'];
  readonly socialConnectors: ExperienceSocialConnector[];
};

const ExistingMain = ({ signInMethods }: Pick<Props, 'signInMethods'>) => {
  const isPasswordOnly = signInMethods.length > 0 &&
    signInMethods.every(({ password, verificationCode }) => password && !verificationCode);

  if (isPasswordOnly) {
    return (
      <PasswordSignInForm
        className={styles.main}
        signInMethods={signInMethods.map(({ identifier }) => identifier)}
      />
    );
  }

  if (signInMethods.length > 0) {
    return <IdentifierSignInForm className={styles.main} signInMethods={signInMethods} />;
  }

  return null;
};

const Main = ({
  method,
  onMethodChange,
  ...props
}: Props & {
  readonly method: 'phone' | 'email';
  readonly onMethodChange: (method: 'phone' | 'email') => void;
}) => {
  const id = useId();
  const { invokeSocialSignIn } = useSocial();
  const [pendingProvider, setPendingProvider] = useState<string>();
  const signInMethods = useMemo(() => props.signInMethods.filter(({ identifier }) =>
    identifier === (method === 'phone' ? SignInIdentifier.Phone : SignInIdentifier.Email)
  ), [method, props.signInMethods]);

  return (
    <>
      <div className={styles.methodTabs} role="tablist" aria-label="Способ входа">
        {(['phone', 'email'] as const).map((value) => (
          <button
            key={value}
            id={`${id}-${value}`}
            type="button"
            role="tab"
            aria-selected={method === value}
            aria-controls={`${id}-panel`}
            onClick={() => onMethodChange(value)}
          >
            {value === 'phone' ? <MessageSquareTextIcon aria-hidden="true" focusable="false" /> : <MailIcon aria-hidden="true" focusable="false" />}
            <span>{value === 'phone' ? 'По SMS' : 'По почте'}</span>
          </button>
        ))}
      </div>
      <div className={`${styles.methodTabs} ${styles.socialButtons}`} role="group" aria-label="Другие способы входа">
        {[
          { target: 'vkid', name: 'VK ID', icon: <VkIdIcon aria-hidden="true" focusable="false" className={styles.vkLogo} /> },
          { target: 'yandex', name: 'Яндекс ID', icon: <YandexIdLogo aria-hidden="true" focusable="false" className={styles.yandexLogo} /> },
        ].map(({ target, name, icon }) => {
          const connector = props.socialConnectors.find((item) => item.target === target);
          return (
            <button key={target} type="button" aria-label={`Войти через ${name}`}
              disabled={!connector || Boolean(pendingProvider)} aria-busy={pendingProvider === target}
              title={connector ? undefined : 'Настройка входа ещё не завершена'}
              onClick={async () => {
                if (!connector || pendingProvider) return;
                setPendingProvider(target);
                try { await invokeSocialSignIn(connector); }
                finally { setPendingProvider(undefined); }
              }}>
              {icon}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${method}`}>
        <ExistingMain key={method} signInMethods={signInMethods} />
      </div>
    </>
  );
};

export default Main;
